import { getFullDate } from '@/helpers/date.helpers'
import { MessageModel, TemplateType } from '@/models/message.model'
import { sendMessageToZApiMultiService } from '@/redux/message/sagas/createMessageSaga'
import {
  SendMessageOfficialAPIResponse,
  SendMessageResponse
} from '@/redux/message/sagas/createMessageSaga/http/message/types'
import { differenceInMilliseconds } from 'date-fns'
import { put } from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'
import { t } from 'i18next'
import { clearActiveChatAction } from '../../actions'
import { transferChatAction } from '../../handles/transferChatAction'
import { refreshChatsCountAction } from '../../handles'
import { Chat } from '../../types'
import { CreateNewMessageDTO, HandleCustomMessageDTO } from './types'
import { sendMessageOfficialAPI } from '@/redux/message/sagas/createMessageSaga/http/message'
import { buildOfficialMessage } from '../createNewChatSaga/helpers'
import { buildUnOfficialMessage } from '@/redux/message/sagas/createMessageSaga/helpers'
import { ChatModel } from '@/models/chat.model'
import { generateUniqueTicketNumber } from '@/redux/message/utils'
import { getTotalChatDuration } from '@/components/ChatMain/components/Header/components/Timer/helpers'
import { save } from '../_http/message'
import { create, update } from '../_http/chat'
import { updateChatDurationsOnClose } from '../closeChatSaga'

export function* transferChatSaga(action: ReturnType<typeof transferChatAction.request>) {
  const { currentChat, currentOperator, newDepartment, currentCampaign, configChat, shareHistory, note, callBack } =
    action.payload

  if (!shareHistory) {
    yield* transferWithoutHistory({ currentChat, currentOperator, newDepartment, note, callBack })
    return
  }

  try {
    let sendedMessage = null
    if (!configChat?.isNotSendTransferToDepartmentMessage) {
      sendedMessage = yield handleCustomMessage({
        campaign: currentCampaign,
        currentChat,
        currentOperator,
        newDepartment
      })
    }
    updateChatDurationsOnTransfer(currentChat, currentOperator.id)
    const dateNow = new Date().getTime()
    const updatedChatData: Chat = {
      ...currentChat,
      ...(sendedMessage && { lastMessage: sendedMessage }),
      department: newDepartment,
      departmentId: newDepartment.id,
      status: 'open',
      operators: [],
      operatorsId: [],
      endDate: dateNow,
      duration: differenceInMilliseconds(new Date(dateNow), new Date(currentChat.startDate)),
      operatorsHistories: currentChat?.operatorsHistories?.length
        ? [
            ...currentChat?.operatorsHistories,
            { id: currentOperator.id, name: currentOperator.name, attend_at: dateNow }
          ]
        : [{ id: currentOperator.id, name: currentOperator.name, attend_at: dateNow }],
      updatedAt: dateNow,
      fixedBy: null
    }

    yield update(currentChat.id, { ...updatedChatData, updatedAt: new Date().getTime() })
    yield put(transferChatAction.success({ transferedChat: updatedChatData }))
    yield put(refreshChatsCountAction())
    if (callBack) callBack()
    yield put(clearActiveChatAction())
  } catch (error) {
    console.log('transferChat error', error)
    yield put(transferChatAction.failure())
  }
}

type TransferWithoutHistoryDTO = {
  currentChat: Chat
  currentOperator: { id: string; name: string }
  newDepartment: Chat['department']
  note?: string
  callBack?: () => void
}

function* transferWithoutHistory({
  currentChat,
  currentOperator,
  newDepartment,
  note,
  callBack
}: TransferWithoutHistoryDTO) {
  try {
    updateChatDurationsOnClose(currentChat, currentOperator.id)
    const dateNow = new Date().getTime()

    const closedChatData: Partial<ChatModel> = {
      id: currentChat.id,
      status: 'close',
      endDate: dateNow,
      duration: getTotalChatDuration(currentChat.durations),
      durations: currentChat.durations,
      notShareHistory: true,
      finishedBy: { id: currentOperator.id, name: currentOperator.name },
      fixedBy: null,
      updatedAt: dateNow
    }

    const baseDate = new Date(dateNow)
    const newChat: Partial<ChatModel> = {
      id: uuid(),
      ownerId: currentChat.ownerId,
      campaignId: currentChat.campaignId,
      instanceId: currentChat.instanceId,
      contactId: currentChat.contactId,
      isGroup: currentChat.isGroup,
      isContact: currentChat.isContact,
      receiverNumber: currentChat.receiverNumber,
      receiverId: currentChat.receiverId,
      requesterNumber: currentChat.requesterNumber,
      requesterPhoto: currentChat.requesterPhoto,
      chatName: currentChat.chatName,
      chatNameLowercase: currentChat.chatNameLowercase,
      department: newDepartment,
      departmentId: newDepartment.id,
      status: 'open',
      unreadMessagesCount: 0,
      operators: [],
      operatorsId: [],
      startDate: dateNow,
      endDate: null,
      month: baseDate.getMonth(),
      year: baseDate.getFullYear(),
      fullDate: getFullDate(baseDate),
      isFinishedUra: false,
      duration: 0,
      durations: [],
      ticket: generateUniqueTicketNumber(),
      lastMessage: null,
      isTransfered: true,
      notShareHistory: false,
      ...(note?.trim() && {
        notes: [
          {
            id: uuid(),
            title: t('translation:transferChatModalAutoNoteTitle'),
            content: note,
            operator: { id: currentOperator.id, name: currentOperator.name },
            createdAt: dateNow,
            updatedAt: dateNow
          }
        ]
      }),
      createdAt: dateNow,
      updatedAt: dateNow
    }

    yield update(currentChat.id, closedChatData)
    yield create(newChat as ChatModel)
    yield put(transferChatAction.success({ transferedChat: { ...currentChat, ...closedChatData } }))
    yield put(refreshChatsCountAction())
    if (callBack) callBack()
    yield put(clearActiveChatAction())
  } catch (error) {
    console.log('transferChatWithoutHistory error', error)
    yield put(transferChatAction.failure())
  }
}

async function handleCustomMessage({ campaign, currentChat, currentOperator, newDepartment }: HandleCustomMessageDTO) {
  const newMessage = await createNewMessage({
    campaign,
    currentChat,
    newDepartment,
    senderId: currentOperator.id
  })

  if (campaign?.isOfficial) {
    const sendMessageOfficialAPIResponse: SendMessageOfficialAPIResponse = await sendMessageOfficialAPI({
      campaignId: campaign.id,
      messageId: newMessage?.templateMessageId,
      phone: newMessage.phone,
      typeSent: newMessage.messageTypeSent,
      phoneNumberId: campaign.phoneNumberId,
      text: newMessage.messageText,
      messageType: newMessage.typeFile
    })
    const sendedMessages = buildOfficialMessage(newMessage, sendMessageOfficialAPIResponse)
    const lastMessage = sendedMessages[sendedMessages.length - 1]
    await save(lastMessage)
    return lastMessage
  } else {
    const sendMessageResult: SendMessageResponse = await sendMessageToZApiMultiService(
      newMessage,
      campaign.id,
      campaign.token
    )
    const sendedMessages = buildUnOfficialMessage(newMessage, sendMessageResult)
    const lastMessage = sendedMessages[sendedMessages.length - 1]
    await save(lastMessage)
    return lastMessage
  }
}

async function createNewMessage({
  campaign,
  currentChat,
  newDepartment,
  senderId
}: CreateNewMessageDTO): Promise<MessageModel> {
  return {
    campanhaId: campaign.id,
    ownerId: campaign.ownerId,
    files: [],
    typeFile: 'text',
    messageText: `Atendimento sendo transferido para o departamento *${newDepartment.name}*, aguarde um momento.`,
    messageTypeSent: TemplateType.SOMENTE_TEXTO_SEM_TEMPLATE_SEM_PARAMETRO,
    senderNumber: currentChat.requesterNumber,
    phone: currentChat.requesterNumber,
    senderId,
    fromMe: true,
    connectedPhone: currentChat.receiverNumber,
    receiverNumber: currentChat.receiverNumber,
    chatId: currentChat.id,
    messageId: '',
    isRead: false,
    isSend: true,
    createdAt: new Date().getTime()
  } as MessageModel
}

function updateChatDurationsOnTransfer(activeChat: ChatModel, operatorId: string) {
  const operatorDuration = findOperatorDuration(activeChat, operatorId)
  if (operatorDuration) {
    const currentPeriod = operatorDuration.periods.at(-1)
    if (currentPeriod && !currentPeriod?.endTime) {
      currentPeriod.endTime = Date.now()
    }
  }
}

function findOperatorDuration(activeChat: ChatModel, operatorId: string) {
  const durations = activeChat?.durations ?? []
  const operatorDuration = durations.find(duration => duration.operatorId === operatorId)
  return operatorDuration
}
